/**
 * PcmRecorder — clean PCM capture via Web Audio.
 *
 * The core Phase 1 fix: getUserMedia with browser DSP (echo cancellation,
 * noise suppression, auto gain) OFF, raw Float32 frames captured in an
 * AudioWorklet (with a raw-PCM Safari fallback), encoded as real 16-bit WAV at
 * the actual context rate. No MediaRecorder, no Opus, no resampling.
 */

import { encodeWav } from './wav';
import { PCM_CAPTURE_WORKLET_URL } from './worklet/pcmCaptureWorklet';

export interface MicInfo {
  sampleRate: number;     // input-reported rate; TakeStats has the context rate
  dspSuppressed: boolean; // true if the UA honored the DSP-off constraints
  deviceLabel: string;
}

export interface TakeStats {
  rmsDb: number;          // of the ENCODED file (post makeup gain)
  peakDb: number;
  clippedFrames: number;  // samples at/above clip threshold
  sampleRate: number;
  dspSuppressed: boolean;
  makeupGainDb: number;   // scalar gain applied at encode (0 = none)
  rawRmsDb: number;       // true input level before makeup gain
}

export type LevelCallback = (rms: number, peak: number, clipped: boolean) => void;

export type RecorderInitFailureKind = 'permission' | 'device' | 'engine';

export class RecorderInitError extends Error {
  readonly kind: RecorderInitFailureKind;

  constructor(kind: RecorderInitFailureKind) {
    super(`Recorder initialization failed: ${kind}`);
    this.name = 'RecorderInitError';
    this.kind = kind;
    Object.setPrototypeOf(this, RecorderInitError.prototype);
  }
}

export function recorderInitFailureKind(error: unknown): RecorderInitFailureKind {
  if (error instanceof RecorderInitError) return error.kind;
  const name = typeof error === 'object' && error !== null && 'name' in error
    ? String((error as { name?: unknown }).name || '')
    : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'permission';
  if (
    name === 'NotFoundError'
    || name === 'NotReadableError'
    || name === 'AbortError'
    || name === 'OverconstrainedError'
  ) return 'device';
  return 'engine';
}

interface CapturePort {
  onmessage: ((event: MessageEvent) => void) | null;
  postMessage(message: { cmd: 'start' | 'stop' }): void;
}

interface CaptureNode {
  input: AudioNode;
  port: CapturePort;
  connect(destination: AudioNode): AudioNode;
  disconnect(): void;
}

/**
 * Narrow raw-PCM fallback for Safari versions whose AudioWorklet module cannot
 * start. It preserves the recorder protocol and never encodes with
 * MediaRecorder/Opus. ScriptProcessor runs on the main thread, so it is used
 * only when the AudioWorklet path fails.
 */
class ScriptProcessorCaptureNode implements CaptureNode {
  readonly input: AudioNode;
  readonly port: CapturePort;
  private readonly processor: ScriptProcessorNode;
  private capturing = false;

  constructor(ctx: AudioContext) {
    this.processor = ctx.createScriptProcessor(4096, 1, 1);
    this.input = this.processor;
    this.port = {
      onmessage: null,
      postMessage: (message) => {
        if (message.cmd === 'start') {
          this.capturing = true;
          Promise.resolve().then(() => this.emit({ type: 'started' }));
        } else {
          this.capturing = false;
          Promise.resolve().then(() => this.emit({ type: 'stopped' }));
        }
      },
    };
    this.processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      let sum = 0;
      let peak = 0;
      let clipped = 0;
      for (let index = 0; index < input.length; index += 1) {
        const value = input[index];
        const absolute = Math.abs(value);
        sum += value * value;
        if (absolute > peak) peak = absolute;
        if (absolute >= 0.985) clipped += 1;
      }
      this.emit({
        type: 'level',
        rms: input.length ? Math.sqrt(sum / input.length) : 0,
        peak,
        clipped,
      });
      if (this.capturing && input.length) {
        const samples = new Float32Array(input.length);
        samples.set(input);
        this.emit({ type: 'frames', samples });
      }
      for (let channel = 0; channel < event.outputBuffer.numberOfChannels; channel += 1) {
        event.outputBuffer.getChannelData(channel).fill(0);
      }
    };
  }

  connect(destination: AudioNode): AudioNode {
    return this.processor.connect(destination);
  }

  disconnect(): void {
    this.processor.onaudioprocess = null;
    this.processor.disconnect();
  }

  private emit(data: Record<string, unknown>): void {
    this.port.onmessage?.({ data } as MessageEvent);
  }
}

export class PcmRecorder {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: CaptureNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private mute: GainNode | null = null;

  private chunks: Float32Array[] = [];
  private recording = false;
  private startedAt = 0;
  private levelCb: LevelCallback | null = null;
  private dspSuppressed = false;
  private deviceLabel = '';
  private startResolve: ((started: boolean) => void) | null = null;
  private stopFlushResolve: (() => void) | null = null;

  async init(): Promise<MicInfo> {
    try {
      // Permission acquisition is intentionally separate from Web Audio
      // activation. Safari's native prompt is an asynchronous boundary and can
      // suspend a context that was unlocked by the original tap. The next
      // explicit sound-check/record tap starts the engine with fresh activation.
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
          sampleRate: 48000,
        },
      });
      const track = this.stream.getAudioTracks()[0];
      if (!track) throw new RecorderInitError('device');
      const s = track.getSettings();
      // Some mobile browsers ignore the DSP-off constraints; record the truth
      // so the backend can tag the sample rather than silently degrade the pool.
      this.dspSuppressed =
        s.echoCancellation !== true &&
        s.noiseSuppression !== true &&
        s.autoGainControl !== true;
      this.deviceLabel = track.label || 'unknown';

      return {
        sampleRate: typeof s.sampleRate === 'number' ? s.sampleRate : 0,
        dspSuppressed: this.dspSuppressed,
        deviceLabel: this.deviceLabel,
      };
    } catch (error) {
      const failure = new RecorderInitError(recorderInitFailureKind(error));
      this.dispose();
      throw failure;
    }
  }

  onLevel(cb: LevelCallback): void {
    this.levelCb = cb;
  }

  async start(): Promise<void> {
    if (!this.stream) throw new Error('PcmRecorder.init() first');
    await this.ensureEngine();
    if (!this.node || !this.ctx) throw new RecorderInitError('engine');
    if (this.ctx.state !== 'running') {
      throw new Error('The microphone was paused by the browser. Tap record and try again.');
    }

    this.chunks = [];
    this.recording = true;
    this.startedAt = performance.now();
    const started = new Promise<boolean>((resolve) => {
      this.startResolve = resolve;
      setTimeout(() => {
        if (this.startResolve) {
          this.startResolve = null;
          resolve(false);
        }
      }, 1000);
    });
    this.node.port.postMessage({ cmd: 'start' });
    if (!(await started)) {
      this.recording = false;
      this.node.port.postMessage({ cmd: 'stop' });
      throw new Error('The browser did not start audio capture. Tap record and try again.');
    }
  }

  async stop(): Promise<{ wavBlob: Blob; durationS: number; stats: TakeStats }> {
    if (!this.node || !this.ctx) throw new Error('PcmRecorder.init() first');
    // The worklet flushes remaining frames, THEN posts 'stopped' — message
    // order on the port guarantees the final frames arrive before the ack.
    const flushed = new Promise<boolean>((resolve) => {
      this.stopFlushResolve = () => resolve(true);
      setTimeout(() => {
        if (this.stopFlushResolve) {
          this.stopFlushResolve = null;
          resolve(false);
        }
      }, 1500);
    });
    this.node.port.postMessage({ cmd: 'stop' });
    const flushConfirmed = await flushed;
    this.recording = false;
    if (!flushConfirmed) {
      throw new Error('Audio capture stopped unexpectedly. Your recording was not saved; please retry.');
    }

    const total = this.chunks.reduce((n, c) => n + c.length, 0);
    const samples = new Float32Array(total);
    let off = 0;
    for (const c of this.chunks) {
      samples.set(c, off);
      off += c.length;
    }
    this.chunks = [];

    const sampleRate = this.ctx.sampleRate;
    assertUsableCapture(total, sampleRate);

    // Makeup gain: with browser AGC deliberately OFF (it wrecks clone
    // quality), honest mic levels sit far below old AGC-boosted captures
    // (-35..-47 dB RMS at reading distance). A pure scalar to peak -1 dB
    // before 16-bit encode recovers bit depth and normal QA levels without
    // any of AGC's pumping — the identity-preserving equivalent.
    const raw = computeStats(samples);
    let makeupGainDb = 0;
    const targetPeak = 10 ** (-1 / 20);
    const peakLin = 10 ** (raw.peakDb / 20);
    if (peakLin > 1e-5 && peakLin < targetPeak) {
      const gain = Math.min(targetPeak / peakLin, 10 ** (30 / 20)); // cap +30 dB
      for (let i = 0; i < samples.length; i++) samples[i] *= gain;
      makeupGainDb = 20 * Math.log10(gain);
    }
    const encoded = computeStats(samples);

    return {
      wavBlob: encodeWav(samples, sampleRate),
      durationS: total / sampleRate,
      stats: {
        ...encoded,
        sampleRate,
        dspSuppressed: this.dspSuppressed,
        makeupGainDb: Math.round(makeupGainDb * 10) / 10,
        rawRmsDb: raw.rmsDb,
      },
    };
  }

  dispose(): void {
    const node = this.node;
    const source = this.source;
    const mute = this.mute;
    const stream = this.stream;
    const ctx = this.ctx;
    this.recording = false;
    this.chunks = [];
    this.levelCb = null;
    this.startResolve = null;
    this.stopFlushResolve = null;
    this.ctx = null;
    this.node = null;
    this.source = null;
    this.mute = null;
    this.stream = null;
    try { node?.port.postMessage({ cmd: 'stop' }); } catch { /* best-effort teardown */ }
    try { node?.disconnect(); } catch { /* best-effort teardown */ }
    try { source?.disconnect(); } catch { /* best-effort teardown */ }
    try { mute?.disconnect(); } catch { /* best-effort teardown */ }
    stream?.getTracks().forEach((track) => {
      try { track.stop(); } catch { /* best-effort teardown */ }
    });
    try { if (ctx && ctx.state !== 'closed') void ctx.close(); } catch { /* best-effort teardown */ }
  }

  private async ensureEngine(): Promise<void> {
    if (this.node && this.ctx) {
      try {
        if (this.ctx.state !== 'running') await this.ctx.resume();
        if (this.ctx.state !== 'running') throw new RecorderInitError('engine');
        return;
      } catch {
        throw new RecorderInitError('engine');
      }
    }
    if (!this.stream) throw new Error('PcmRecorder.init() first');

    try {
      // This method begins synchronously inside the explicit sound-check or
      // record tap. Construct/resume before the first await so Safari sees the
      // current user activation, not the earlier permission-prompt gesture.
      this.ctx = new AudioContext();
      if ((this.ctx.state as AudioContextState) === 'suspended') await this.ctx.resume();
      if ((this.ctx.state as AudioContextState) !== 'running') throw new RecorderInitError('engine');

      const audioWorklet = (this.ctx as AudioContext & {
        audioWorklet?: { addModule(url: string): Promise<void> };
      }).audioWorklet;
      if (audioWorklet && typeof AudioWorkletNode !== 'undefined') {
        try {
          await audioWorklet.addModule(PCM_CAPTURE_WORKLET_URL);
          const workletNode = new AudioWorkletNode(this.ctx, 'pcm-capture', {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            channelCount: 1,
          });
          this.node = {
            input: workletNode,
            port: workletNode.port,
            connect: (destination) => workletNode.connect(destination),
            disconnect: () => workletNode.disconnect(),
          };
        } catch {
          this.node = null;
        }
      }
      if (!this.node) {
        if (typeof this.ctx.createScriptProcessor !== 'function') {
          throw new RecorderInitError('engine');
        }
        this.node = new ScriptProcessorCaptureNode(this.ctx);
      }
      this.node.port.onmessage = (event: MessageEvent) => this.onWorkletMessage(event);
      this.source = this.ctx.createMediaStreamSource(this.stream);
      this.source.connect(this.node.input);
      // Keep either capture backend in the graph while emitting silence.
      this.mute = this.ctx.createGain();
      this.mute.gain.value = 0;
      this.node.connect(this.mute);
      this.mute.connect(this.ctx.destination);
    } catch {
      this.disposeEngine();
      throw new RecorderInitError('engine');
    }
  }

  private disposeEngine(): void {
    const node = this.node;
    const source = this.source;
    const mute = this.mute;
    const ctx = this.ctx;
    this.node = null;
    this.source = null;
    this.mute = null;
    this.ctx = null;
    try { node?.disconnect(); } catch { /* best-effort teardown */ }
    try { source?.disconnect(); } catch { /* best-effort teardown */ }
    try { mute?.disconnect(); } catch { /* best-effort teardown */ }
    try { if (ctx && ctx.state !== 'closed') void ctx.close(); } catch { /* best-effort teardown */ }
  }

  private onWorkletMessage(e: MessageEvent): void {
    const msg = e.data;
    if (msg.type === 'started') {
      this.startResolve?.(true);
      this.startResolve = null;
    } else if (msg.type === 'level') {
      this.levelCb?.(msg.rms, msg.peak, msg.clipped > 0);
    } else if (msg.type === 'frames') {
      if (this.recording) this.chunks.push(msg.samples as Float32Array);
    } else if (msg.type === 'stopped') {
      this.stopFlushResolve?.();
      this.stopFlushResolve = null;
    }
  }
}

export function assertUsableCapture(totalFrames: number, sampleRate: number): void {
  const minimumFrames = Math.max(1, Math.floor(sampleRate * 0.1));
  if (totalFrames < minimumFrames) {
    throw new Error('No microphone audio was captured. Your recording was not saved; please retry.');
  }
}

function computeStats(
  samples: Float32Array
): Omit<TakeStats, 'sampleRate' | 'dspSuppressed' | 'makeupGainDb' | 'rawRmsDb'> {
  let sum = 0;
  let peak = 0;
  let clippedFrames = 0;
  for (let i = 0; i < samples.length; i++) {
    const a = Math.abs(samples[i]);
    sum += samples[i] * samples[i];
    if (a > peak) peak = a;
    if (a >= 0.985) clippedFrames++;
  }
  const rms = samples.length ? Math.sqrt(sum / samples.length) : 0;
  const toDb = (v: number) => (v <= 1e-7 ? -140 : 20 * Math.log10(v));
  return { rmsDb: toDb(rms), peakDb: toDb(peak), clippedFrames };
}
